import * as O from 'fp-ts/Option';
import * as Hooks from 'preact/hooks';
import { useDispatch } from 'react-redux';
import * as ArrayString from '../../data/arrayString';
import * as LoadingStore from '../../store/loading';
import * as CoreStore from '../../store/core';
import * as Selectors from '../../store/selectors';
import * as Logging from '../../store/actions/logging';
import { useSelector } from './redux';

/** Determine whether Dora app is still loading anything */
export function useLoadingState() {
    const furnitureList = useSelector(Selectors.FurnitureList.furnitureList);
    const whatIsLoading = useSelector(Selectors.Loading.whatIsLoading);
    const simpleFurnitureInSet = useSelector(Selectors.Loading.totalSimpleFurnitureToLoad);
    const modularFurnitureInSet = useSelector(Selectors.Loading.totalModularFurnitureToLoad);
    const productIdToLoad = useSelector(Selectors.Loading.singleProductIdToLoad);
    const currentlyLoadingFlag = useSelector(Selectors.Loading.currentlyLoading);
    const appLoading = useSelector(Selectors.Core.loading);
    const launchData = useSelector(Selectors.Core.launchData);
    const open = useSelector(Selectors.Core.open);
    const dispatch = useDispatch();

    Hooks.useEffect(() => {
        if (!launchData && open) {
            // if there's no space launch data when the scene is open, then there is nothing to load!
            dispatch(CoreStore.setLoadingComplete());
            return;
        }

        const isAllSimpleFurnitureInScene = () =>
            simpleFurnitureInSet?.every(furniture =>
                furnitureList.some(f =>
                    ArrayString.eq(
                        f.productIds,
                        furniture.products.map(x => x.productId),
                    ),
                ),
            ) ?? false;

        const isModularFurnitureInScene = () =>
            modularFurnitureInSet.length ===
            furnitureList.filter(f => O.isSome(f.configuration)).length;

        const isProductInScene = () =>
            productIdToLoad
                ? Boolean(
                      furnitureList.find(
                          f => f.productIds.length === 1 && f.productIds.includes(productIdToLoad),
                      ),
                  )
                : false;

        if (whatIsLoading !== null && whatIsLoading !== 'product' && whatIsLoading !== 'layout') {
            dispatch(
                Logging.logWarning(
                    'WARNING: received loading state which is not handled. Handled states: product, layout. Failure to handle may result in app never exiting the initial load state.',
                    { loadingState: whatIsLoading },
                ),
            );
        }

        const sceneLoaded =
            whatIsLoading === 'product'
                ? isProductInScene()
                : whatIsLoading === 'layout'
                ? isAllSimpleFurnitureInScene() && isModularFurnitureInScene()
                : false;

        if (sceneLoaded && currentlyLoadingFlag) {
            // report we're done once everything is loaded
            dispatch(LoadingStore.completeLoading());
            dispatch(CoreStore.setLoadingComplete());
        }
    }, [furnitureList, currentlyLoadingFlag]);

    return appLoading;
}
